import React, { useContext, useState } from 'react';
import { ThemeContext } from '../hooks/ThemeContext';
import { UserContext } from '../hooks/UserContext';
import { Link } from 'react-router-dom';
import { 
  Palette, 
  TrendingUp, 
  Shield, 
  DollarSign, 
  Users, 
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/baseUrl';

import PropTypes from 'prop-types';

// Formulaire d'inscription créateur avec envoi de mail explicatif
const BecomeCreatorForm = ({ isDark }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    motivation: '',
    stripeEmail: '',
    accountName: '',
    address: '',
    siret: ''
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Vérification SIRET
      if (!form.siret || form.siret.length < 14) {
        toast.error("Le numéro de SIRET est obligatoire pour devenir créateur et recevoir des paiements.");
        setLoading(false);
        return;
      }
      const payload = { ...form };
      delete payload.iban;
      await api.post('/creator-request', payload);
      setSent(true);
      toast.success("Votre demande a bien été envoyée. Vous recevrez un mail explicatif sous peu.");
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
      // Optionnel : log l'erreur pour debug
      // console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Lien d'onboarding Stripe Connect
  const handleStripeOnboarding = async () => {
    setStripeLoading(true);
    try {
      const res = await api.post('/creators/stripe-onboarding');
      if (res.data && res.data.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Erreur lors de la génération du lien Stripe.");
      }
    } catch (err) {
      toast.error("Erreur lors de la connexion à Stripe.");
    } finally {
      setStripeLoading(false);
    }
  };

  if (sent) {
    return (
      <div className={`w-full border shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-white' : 'border-gray-200 bg-white text-gray-900'} p-8 rounded-3xl`}>
        <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
          <CheckCircle className="w-7 h-7 text-rose-400" />
        </div>
        <div className="space-y-4">
          <div>
            <h3 className="text-2xl font-bold mb-2">Demande envoyée</h3>
            <p className={`text-sm leading-7 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Vous recevrez un rappel du fonctionnement du reversement 95% / 5%.
              <br />
              Pour recevoir vos 95%, connectez votre compte Stripe ci-dessous.
            </p>
          </div>
          <div className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'border-rose-100 bg-rose-50'}`}>
            <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Paiements sécurisés, reversement réel via Stripe Connect et onboarding rapide pour commencer à vendre dans de bonnes conditions.
            </p>
          </div>
          <div>
            <button
              type="button"
              onClick={handleStripeOnboarding}
              disabled={stripeLoading}
              className="inline-flex items-center justify-center px-6 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 transition-colors font-semibold disabled:opacity-60 shadow-xl"
            >
              {stripeLoading ? 'Redirection vers Stripe...' : 'Connecter mon compte Stripe'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className={`w-full border shadow-xl ${isDark ? 'bg-white/5 text-white border-white/10' : 'bg-white text-gray-900 border-gray-200'} p-8 lg:p-10 rounded-3xl flex flex-col gap-7`}>
        <div className="space-y-4 text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
            <Palette className="w-8 h-8 text-rose-400" />
          </div>
          <div>
            <h2 className="text-3xl font-extrabold tracking-tight">Devenir Créateur</h2>
            <p className={`mt-2 text-sm leading-7 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Rejoignez la marketplace premium des créateurs digitaux avec une expérience claire, sécurisée et conçue pour convertir.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="name" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Nom complet</label>
            <input
              id="name"
              type="text"
              name="name"
              required
              autoComplete="name"
              placeholder="Votre nom complet"
              className={`px-4 py-3 rounded-2xl border outline-none transition-all font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.name}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="siret" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Numéro de SIRET <span className="text-rose-500">*</span></label>
            <input
              id="siret"
              type="text"
              name="siret"
              required
              autoComplete="off"
              placeholder="SIRET (14 chiffres)"
              className={`px-4 py-3 rounded-2xl border outline-none transition-all tracking-widest font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.siret}
              onChange={handleChange}
              maxLength={14}
              minLength={14}
              pattern="[0-9]{14}"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="email" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Email</label>
            <input
              id="email"
              type="email"
              name="email"
              required
              autoComplete="email"
              placeholder="Votre email"
              className={`px-4 py-3 rounded-2xl border outline-none transition-all font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.email}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="stripeEmail" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Email Stripe</label>
            <input
              id="stripeEmail"
              type="email"
              name="stripeEmail"
              required
              autoComplete="off"
              placeholder="Email Stripe (pour recevoir les paiements)"
              className={`px-4 py-3 rounded-2xl border outline-none transition-all font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.stripeEmail}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label htmlFor="accountName" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Nom du compte bancaire</label>
            <input
              id="accountName"
              type="text"
              name="accountName"
              required
              autoComplete="off"
              placeholder="Nom du compte bancaire (pour les virements)"
              className={`px-4 py-3 rounded-2xl border outline-none transition-all font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.accountName}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label htmlFor="address" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Adresse complète</label>
            <input
              id="address"
              type="text"
              name="address"
              required
              autoComplete="off"
              placeholder="Adresse complète (pour la facturation Stripe)"
              className={`px-4 py-3 rounded-2xl border outline-none transition-all font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.address}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label htmlFor="motivation" className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-700'}`}>Motivation</label>
            <textarea
              id="motivation"
              name="motivation"
              required
              placeholder="Expliquez votre motivation et votre expérience (facultatif)"
              className={`px-4 py-3 rounded-2xl border min-h-[120px] outline-none transition-all font-medium ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-gray-500 focus:border-rose-400 focus:bg-white/[0.08]' : 'bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-rose-400 focus:bg-rose-50'}`}
              value={form.motivation}
              onChange={handleChange}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-4 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 transition-colors font-semibold mt-2 disabled:opacity-60 shadow-xl text-lg tracking-wide"
        >
          {loading ? 'Envoi en cours...' : 'Demander à devenir créateur'}
        </button>
        <div className={`rounded-2xl border p-4 flex gap-3 items-start text-sm ${isDark ? 'border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 text-gray-200' : 'border-yellow-200 bg-yellow-50 text-gray-700'}`}>
          <Shield className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold">Sécurité & conformité :</span> SIRET valide obligatoire, vérification manuelle et transactions sécurisées via Stripe Connect.
            <br />
            <span className="font-semibold">Entreprise uniquement :</span> l’inscription créateur est réservée aux professionnels disposant d’un SIRET valide.
            <br />
            <span className="font-semibold">Vous êtes un particulier ?</span> Créez un statut auto-entrepreneur sur <a href="https://www.autoentrepreneur.urssaf.fr/" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">autoentrepreneur.urssaf.fr</a> pour obtenir un SIRET.
          </div>
        </div>
      </form>
    </div>
  );
};

BecomeCreatorForm.propTypes = {
  isDark: PropTypes.bool.isRequired
};

const DevenirCreateur = () => {
  const { theme } = useContext(ThemeContext);

  const { isAuthenticated, role, user } = useContext(UserContext);
  const isDark = theme === 'dark';

  // Rôles : 1 = user, 2 = créateur, 3 = admin
  // Permettre à l'admin (id_user 1) de faire une demande comme un user normal
  const isAdmin = role === 3;
  const isCreator = role === 2;
  const isAdminUser1 = isAdmin && user?.id_user === 1;

  const benefits = [
    {
      icon: <DollarSign className="w-8 h-8 text-yellow-500" />,
      title: 'Commissions Équitables',
      desc: 'Gardez la majorité de vos revenus avec notre système de commission transparent.'
    },
    {
      icon: <Users className="w-8 h-8 text-blue-500" />,
      title: 'Audience Ciblée',
      desc: 'Accédez à une communauté de clients recherchant activement des créations de qualité.'
    },
    {
      icon: <Shield className="w-8 h-8 text-rose-400" />,
      title: 'Protection Garantie',
      desc: 'Vos créations sont protégées avec filigrane et téléchargement sécurisé.'
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-purple-500" />,
      title: 'Croissance Facilitée',
      desc: 'Outils et support pour développer votre activité et votre portfolio.'
    },
    {
      icon: <Palette className="w-8 h-8 text-rose-400" />,
      title: 'Visibilité Maximale',
      desc: 'Mettez en avant vos créations auprès de milliers de clients potentiels.'
    },
    {
      icon: <CheckCircle className="w-8 h-8 text-rose-500" />,
      title: 'Paiements Sécurisés',
      desc: 'Versements rapides et fiables via Stripe Connect, sécurisés de bout en bout.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Créez votre compte',
      desc: 'Inscrivez-vous gratuitement en tant que créateur sur notre plateforme.'
    },
    {
      number: '2',
      title: 'Configurez votre profil',
      desc: 'Présentez votre travail et définissez vos tarifs.'
    },
    {
      number: '3',
      title: 'Uploadez vos créations',
      desc: 'Ajoutez vos bannières, logos, illustrations et autres créations digitales.'
    },
    {
      number: '4',
      title: 'Commencez à vendre',
      desc: 'Vos créations sont immédiatement disponibles à la vente sur notre marketplace.'
    }
  ];

  const creationTypes = [
    'Bannières YouTube, Twitch, Discord',
    'Logos et identités visuelles',
    'Illustrations digitales',
    'Templates réseaux sociaux',
    'Designs pour stream et gaming',
    'Assets graphiques personnalisés'
  ];

  const complianceItems = [
    {
      title: 'Vérification manuelle',
      desc: 'Chaque dossier est validé sous 24h pour maintenir un niveau premium.'
    },
    {
      title: 'Droits d’auteur',
      desc: 'Créations originales obligatoires, respect des licences et contenus autorisés.'
    },
    {
      title: 'Paiements protégés',
      desc: 'Versements via Stripe Connect, sécurisés avec suivi et traçabilité.'
    },
    {
      title: 'Qualité marketplace',
      desc: 'Contrôle des visuels, formats et fichiers livrés pour une UX irréprochable.'
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      {/* Hero Section */}
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-b border-white/10' : 'bg-gradient-to-br from-rose-50 via-purple-50 to-blue-50 border-b border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 lg:py-20 max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1.02fr_0.98fr] items-start">
            <div className="space-y-6 lg:pr-6">
              <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
                <Palette className="w-4 h-4 text-rose-400" />
                Rejoignez une marketplace créative premium
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl xl:text-6xl font-bold leading-tight">
                  Devenez <span className="text-rose-500">Créateur</span> avec une expérience premium
                </h1>
                <p className={`max-w-2xl text-lg leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Une page claire, rassurante et performante pour candidater, connecter Stripe et lancer vos ventes sans friction.
                </p>
              </div>

              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {[
                  'Inscription 100% gratuite',
                  'Commissions transparentes',
                  'Paiements sécurisés'
                ].map((item) => (
                  <div
                    key={item}
                    className={`rounded-3xl border p-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}
                  >
                    <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                      <CheckCircle className="w-5 h-5 text-rose-400" />
                    </div>
                    <p className="font-medium">{item}</p>
                  </div>
                ))}
              </div>

              <div className={`rounded-3xl border p-6 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-3">
                    <h2 className="text-xl font-semibold">Pourquoi ça convertit mieux ?</h2>
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Un message plus net, des zones bien hiérarchisées et des éléments de confiance visibles dès l’arrivée sur la page.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {['Support créateurs 24h', 'Onboarding Stripe guidé', 'Validation rapide des dossiers'].map((item) => (
                      <div key={item} className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                          <CheckCircle className="w-4 h-4 text-rose-400" />
                        </div>
                        <span className={`${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full">
              {!isAuthenticated && <BecomeCreatorForm isDark={isDark} />}
              {isAuthenticated && (!isCreator || isAdminUser1) && <BecomeCreatorForm isDark={isDark} />}
              {isAuthenticated && isCreator && !isAdminUser1 && (
                <div className={`rounded-3xl border p-8 lg:p-10 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                  <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                    <CheckCircle className="w-8 h-8 text-rose-400" />
                  </div>
                  <h2 className="text-3xl font-bold mb-3">Vous êtes déjà créateur</h2>
                  <p className={`mb-6 leading-7 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Votre profil est déjà configuré. Accédez directement à votre espace pour gérer vos créations, vos ventes et vos revenus.
                  </p>
                  <Link 
                    to="/espaces-clients" 
                    className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 transition-colors font-semibold shadow-xl"
                  >
                    Accéder à mon espace
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Avantages */}
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="max-w-3xl mb-12">
          <h2 className="text-3xl font-bold mb-4">Pourquoi nous rejoindre ?</h2>
          <p className={`text-lg leading-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Une expérience pensée pour vous aider à vendre plus, rassurer vos clients et valoriser votre travail dès la première visite.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, idx) => (
            <div 
              key={idx}
              className={`p-6 rounded-3xl shadow-xl border transition-all ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/[0.07]' : 'bg-white border-gray-200 hover:shadow-2xl'}`}
            >
              <div className="inline-flex p-3 rounded-2xl mb-5 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                {benefit.icon}
              </div>
              <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
              <p className={`leading-7 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{benefit.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comment ça marche */}
      <div className={`${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-y border-white/10' : 'bg-white border-y border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 max-w-7xl">
          <div className="max-w-3xl mb-12">
            <h2 className="text-3xl font-bold mb-4">Comment ça marche ?</h2>
            <p className={`text-lg leading-8 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              4 étapes simples pour commencer à vendre vos créations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div key={idx} className={`rounded-3xl border p-6 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold mb-5 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 text-rose-400 shadow-xl">
                  {step.number}
                </div>
                <h3 className="text-lg font-semibold mb-3">{step.title}</h3>
                <p className={`text-sm leading-7 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sécurité & conformité */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="max-w-3xl mb-12">
            <h2 className="text-3xl font-bold mb-4">Sécurité, conformité et qualité</h2>
            <p className={`text-lg leading-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Une inscription encadrée pour protéger vos ventes et l'expérience client
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {complianceItems.map((item) => (
            <div
              key={item.title}
              className={`p-6 rounded-3xl shadow-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}
            >
              <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                <Shield className="w-5 h-5 text-rose-400" />
              </div>
              <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
              <p className={`leading-7 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Paiements & commissions */}
      <div className={`${isDark ? 'bg-[#0b0b0c] border-y border-white/10' : 'bg-white border-y border-gray-200'}`}>
        <div className="container mx-auto px-4 py-16 max-w-6xl">
          <div className="max-w-3xl mb-10">
            <h2 className="text-3xl font-bold mb-4">Revenus & paiements</h2>
            <p className={`text-lg leading-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Transparence totale sur la règle de reversement: 95% pour le créateur, 5% pour Krayne Agency.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className={`p-6 rounded-3xl shadow-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-gray-200'}`}>
              <h3 className="text-xl font-semibold mb-4">Récapitulatif commissions & taxes</h3>
              <ul className="text-sm space-y-3 text-left">
                <li><span className="font-semibold">Reversement créateur :</span> 95% du montant encaissé.</li>
                <li><span className="font-semibold">Part plateforme :</span> 5% du montant encaissé.</li>
                <li><span className="font-semibold">Compte de paiement :</span> reversement via Stripe Connect après onboarding validé.</li>
                <li><span className="font-semibold">Fiscalité :</span> chaque partie déclare ses propres revenus sur sa part.</li>
              </ul>
              <p className={`text-xs mt-5 leading-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Le créateur perçoit 95% et gère ses obligations fiscales sur cette part. Krayne Agency conserve 5% et gère ses obligations sur cette part. Le reversement est effectué via Stripe Connect.</p>
            </div>

            <div className={`p-6 rounded-3xl shadow-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-gray-200'}`}>
              <h3 className="text-xl font-semibold mb-4">Exemple de gains</h3>
              <div className={`p-5 rounded-2xl border ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-white/10' : 'bg-white border-gray-200'}`}> 
                <p className="text-sm mb-3">Pour une création vendue 40€ :</p>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Prix de vente</span>
                    <span className="font-semibold">40€</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Part Krayne Agency (5%)</span>
                    <span className="font-semibold">2€</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Part créateur (95%)</span>
                    <span className="font-semibold">38€</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>Reversement Stripe Connect</span>
                    <span className="text-blue-600">38€ vers le créateur</span>
                  </div>
                </div>
              </div>
              <p className={`text-xs mt-5 leading-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Après inscription, vous recevez un email pour lier votre compte Stripe Connect.<br />
                <span className="font-semibold">À noter :</span> Vous êtes responsable de vos déclarations fiscales et sociales sur vos 95%.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Types de créations */}
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="max-w-3xl mb-12">
            <h2 className="text-3xl font-bold mb-4">Que pouvez-vous vendre ?</h2>
            <p className={`text-lg leading-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Une grande variété de créations digitales
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-12">
          {creationTypes.map((type, idx) => (
              <div 
                key={idx}
                className={`p-5 rounded-3xl shadow-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'} flex items-center gap-3`}
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                  <Palette className={`w-5 h-5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
                </div>
                <span>{type}</span>
              </div>
          ))}
        </div>

        {/* CTA Final */}
        <div className={`p-8 md:p-10 rounded-3xl shadow-xl border text-center ${isDark ? 'bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 border-white/10' : 'bg-gradient-to-r from-rose-50 to-purple-50 border-gray-200'}`}>
          <h3 className="text-2xl font-bold mb-4">Prêt à commencer ?</h3>
          <p className={`mb-6 max-w-2xl mx-auto leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Rejoignez des centaines de créateurs qui font confiance à Krayne Agency
          </p>
          {!isAuthenticated && (
            <Link 
              to="/register?role=creator#creator" 
              className="inline-flex items-center gap-2 px-8 py-4 bg-rose-600 text-white rounded-2xl hover:bg-rose-700 transition-colors font-semibold text-lg shadow-xl"
            >
              Inscription créateur
              <ArrowRight className="w-5 h-5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevenirCreateur;
